'use client'

import { useState, useEffect } from 'react'

import { useNavigate } from 'react-router-dom'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface DuplicateScheduleModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	scheduleId?: string
	scheduleName?: string
	scheduleDescription?: string
}

export const DuplicateScheduleModal = ({
	open,
	onOpenChange,
	scheduleId,
	scheduleName,
	scheduleDescription,
}: DuplicateScheduleModalProps) => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [newScheduleName, setNewScheduleName] = useState('')

	useEffect(() => {
		if (open && scheduleName) {
			setNewScheduleName(
				`${scheduleName} ${t('entities.schedule.copy_suffix')}`
			)
		}
	}, [open, scheduleName, t])

	const handleDuplicate = async () => {
		if (!scheduleId || !newScheduleName.trim()) return

		const params = new URLSearchParams({
			mode: 'create',
			name: newScheduleName.trim(),
			duplicateFrom: scheduleId,
		})

		if (scheduleDescription?.trim()) {
			params.set('description', scheduleDescription.trim())
		}

		onOpenChange(false)
		setNewScheduleName('')
		navigate(`/schedules/new?${params.toString()}`)
	}

	const handleCancel = () => {
		onOpenChange(false)
		setNewScheduleName('')
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md p-0 gap-0">
				<DialogHeader className="px-6 pt-6 pb-3">
					<DialogTitle className="text-2xl font-semibold">
						{t('entities.schedule.duplicate.title')}
					</DialogTitle>
				</DialogHeader>

				<Separator />

				<div className="px-6 py-6">
					<div className="space-y-2">
						<Label htmlFor="duplicate-name" className="text-base">
							{t('entities.schedule.schedule_name')}
						</Label>
						<Input
							id="duplicate-name"
							placeholder={t('entities.schedule.schedule_name')}
							value={newScheduleName}
							onChange={(e) => setNewScheduleName(e.target.value)}
							className="h-10"
							autoFocus
						/>
					</div>
				</div>

				<Separator />

				<DialogFooter className="px-6 py-4">
					<Button
						type="button"
						variant="outline"
						onClick={handleCancel}
						className="h-9 rounded-full px-6"
					>
						{t('actions.cancel')}
					</Button>
					<Button
						type="button"
						onClick={handleDuplicate}
						disabled={!newScheduleName.trim()}
						className="h-9 rounded-full px-6"
					>
						{t('entities.schedule.duplicate.duplicate')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
